import { getApiUrl } from '../utils/api';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import SummaryCard from './SummaryCard';
import ProductionStatus from './ProductionStatus';
import HelpIcon from './HelpIcon';
import { useInterface } from '../contexts/InterfaceContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { 
  UsersIcon as UsersGroupIcon,
  UserIcon as UserCircleIconMini,
  ChartBarIcon as ComputerDesktopIconMini,
  ChartBarIcon
} from '@heroicons/react/24/outline';

// Final Optimized Dashboard - Complete Implementation
// Single API call, proper React rendering, 1-second updates

interface UnifiedDashboardData {
  version: string;
  timestamp: string;
  responseTime: number;
  data: {
    status: {
      currentTime: string;
      productionDate: string;
      currentShift: {
        number: number;
        name: string;
      };
      lineMode: string;
    };
    session: {
      id: string;
      groupId: string;
      groupName: string;
      lineId?: string;
      productionTime: number;
      columnConfiguration?: any[];
      individualProducts?: Array<{ id: string; name: string }>;
      showIndividualYields?: boolean;
    } | null;
    totals: {
      totalOutfeed: number;
      totalInfeed: number;
      averageYield: number;
      activeWorkers: number;
    };
    stations: Array<any>;
    columns: Array<{
      key: string;
      label: string;
      type: string;
      decimals?: number;
      productId?: string;
    }>;
  };
}

const DashboardFinalOptimized: React.FC = () => {
  const { selectedLineId } = useInterface();
  const { weightUnit } = usePreferences();
  const [dashboardData, setDashboardData] = useState<UnifiedDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'slow' | 'error'>('connected');
  
  // Performance optimization refs
  const lastVersionRef = useRef<string>('');
  const abortControllerRef = useRef<AbortController | null>(null);
  const fetchInProgressRef = useRef<boolean>(false);
  const consecutiveErrorsRef = useRef<number>(0);
  const pollingIntervalRef = useRef<number>(1000);
  const lastResponseTimeRef = useRef<number>(0);
  
  const apiUrl = getApiUrl();

  // Single unified fetch function
  const fetchUnifiedData = useCallback(async () => {
    if (fetchInProgressRef.current) {
      return;
    }
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    fetchInProgressRef.current = true;
    abortControllerRef.current = new AbortController();
    const startTime = performance.now();
    
    try {
      const response = await fetch(
        `${apiUrl}/dashboard/unified?lineId=${selectedLineId || ''}`,
        {
          signal: abortControllerRef.current.signal,
          headers: {
            'If-None-Match': lastVersionRef.current,
            'Accept': 'application/json',
            'Cache-Control': 'no-cache'
          }
        }
      );
      
      const responseTime = performance.now() - startTime;
      lastResponseTimeRef.current = responseTime;
      
      if (response.status === 304) {
        setConnectionStatus('connected');
        consecutiveErrorsRef.current = 0;
        return;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data: UnifiedDashboardData = await response.json();
      
      if (data.version) {
        lastVersionRef.current = data.version;
      }
      
      // CRITICAL: Force React to re-render by creating new object
      setDashboardData(prevData => {
        // Always return a new object to trigger re-render
        try {
          return { ...data };
        } catch (error) {
          console.error('Error updating dashboard data:', error);
          return prevData; // Return previous data on error to prevent crash
        }
      });
      
      setLoading(false);
      setConnectionStatus(responseTime > 500 ? 'slow' : 'connected');
      consecutiveErrorsRef.current = 0;
      
      // Keep fixed 1-second polling - no adaptive polling
      
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return;
      }
      
      consecutiveErrorsRef.current++;
      console.error(`[Dashboard] Error:`, error.message);
      
      if (consecutiveErrorsRef.current > 3) {
        setConnectionStatus('error');
        // Keep fixed 1-second polling even on errors
      }
    } finally {
      fetchInProgressRef.current = false;
    }
  }, [apiUrl, selectedLineId]);

  // Create ref for current fetch function to avoid stale closures
  const fetchFunctionRef = useRef(fetchUnifiedData);
  fetchFunctionRef.current = fetchUnifiedData;

  // Robust polling effect - completely independent of data state
  useEffect(() => {
    let isActive = true;
    let pollingInterval: NodeJS.Timeout | null = null;
    
    const startPolling = async () => {
      // Initial fetch
      if (isActive) {
        try {
          await fetchFunctionRef.current();
        } catch (error) {
          console.error('Initial fetch error:', error);
        }
      }
      
      // Set up consistent polling
      const poll = async () => {
        if (!isActive) return;
        
        try {
          await fetchFunctionRef.current();
        } catch (error) {
          console.error('Polling error:', error);
          // Continue polling even on errors
        }
      };
      
      // Force polling every 1 second regardless of any conditions
      pollingInterval = setInterval(poll, 1000);
      console.log('Dashboard polling started at 1-second intervals');
    };
    
    startPolling();
    
    return () => {
      isActive = false;
      if (pollingInterval) {
        clearInterval(pollingInterval);
        console.log('Dashboard polling stopped');
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []); // No dependencies - completely independent

  // Function to get the real value for each metric type
  const getRealValue = useCallback((type: string) => {
    if (!dashboardData?.data?.totals) return '0.00';

    const totals = dashboardData.data.totals;
    const unitLabel = weightUnit === 'kg' ? 'Kg' : 'Lb';
    const conversionFactor = weightUnit === 'kg' ? 0.453592 : 1;

    switch (type) {
      case 'totalOutfeedWeight':
        return `${(totals.totalOutfeed * conversionFactor).toFixed(2)}`;
      case 'totalInfeedWeight':
        return `${(totals.totalInfeed * conversionFactor).toFixed(2)}`;
      case 'avgEmployeeOutfeed':
        return totals.activeWorkers > 0
          ? `${((totals.totalOutfeed / totals.activeWorkers) * conversionFactor).toFixed(2)}`
          : '0.00';
      case 'avgEmployeeYield':
        return `${totals.averageYield.toFixed(2)}%`;
      default:
        return 'N/A';
    }
  }, [dashboardData, weightUnit]);

  // Function to get the appropriate icon for each metric type
  const getMetricIcon = useCallback((iconType: string) => {
    switch (iconType) {
      case 'scale':
        return <ChartBarIcon className="h-6 w-6" />;
      case 'users':
        return <UsersGroupIcon className="h-6 w-6" />;
      case 'user':
        return <UserCircleIconMini className="h-6 w-6" />;
      case 'chart':
        return <ChartBarIcon className="h-6 w-6" />;
      default:
        return <ChartBarIcon className="h-6 w-6" />;
    }
  }, []);

  // Process summary cards from dashboardMetrics configuration
  const summaryCards = useMemo(() => {
    const session = dashboardData?.data?.session;
    const dashboardMetrics = session?.dashboardMetrics;
    const unitLabel = weightUnit === 'kg' ? 'Kg' : 'Lb';

    // If no configuration, use default cards
    if (!dashboardMetrics || dashboardMetrics.length === 0) {
      return [
        {
          id: 'totalSystemLb',
          title: `Total System ${unitLabel}`,
          value: getRealValue('totalOutfeedWeight'),
          icon: <UsersGroupIcon className="h-6 w-6" />,
          iconBgColor: 'bg-green-100',
          iconColor: 'text-green-600'
        },
        {
          id: 'averageWorkerLb',
          title: `Average Worker ${unitLabel}`,
          value: getRealValue('avgEmployeeOutfeed'),
          icon: <UserCircleIconMini className="h-6 w-6" />,
          iconBgColor: 'bg-sky-100',
          iconColor: 'text-sky-600'
        },
        {
          id: 'averageSystemYield',
          title: 'Average System Yield',
          value: getRealValue('avgEmployeeYield'),
          icon: <ComputerDesktopIconMini className="h-6 w-6" />,
          iconBgColor: 'bg-fuchsia-100',
          iconColor: 'text-fuchsia-600'
        }
      ];
    }

    // Generate cards from dashboardMetrics configuration
    return dashboardMetrics
      .filter((metric: any) => metric.enabled)
      .map((metric: any) => {
        // Add unit label to display name if it's a weight metric
        let displayName = metric.displayName || metric.type;
        if (metric.type === 'totalOutfeedWeight' || metric.type === 'totalInfeedWeight' || metric.type === 'avgEmployeeOutfeed') {
          displayName = displayName.includes(unitLabel) ? displayName : `${displayName} ${unitLabel}`;
        }

        return {
          id: metric.id,
          title: displayName,
          value: getRealValue(metric.type),
          icon: getMetricIcon(metric.icon),
          iconBgColor: metric.iconBgColor || 'bg-gray-100',
          iconColor: metric.iconColor || 'text-gray-600'
        };
      });
  }, [dashboardData, weightUnit, getRealValue, getMetricIcon]);

  // Format cell value based on column type
  const formatCellValue = useCallback((value: any, column: any) => {
    if (value === null || value === undefined) return '0.00';
    
    if (column.type === 'number' && column.decimals !== undefined) {
      const numValue = parseFloat(value) || 0;
      if (column.key.includes('weight') && weightUnit === 'kg') {
        return (numValue * 0.453592).toFixed(column.decimals);
      }
      return numValue.toFixed(column.decimals);
    }
    
    return value || '';
  }, [weightUnit]);

  if (loading) {
    return (
      <div className="p-4 md:p-8 space-y-6 md:space-y-8 pr-20">
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  const activeSession = dashboardData?.data?.session;
  const dataTableTitle = activeSession 
    ? `${activeSession.groupName} - Production Dashboard`
    : 'Production Dashboard';

  return (
    <div className="p-4 md:p-8 space-y-6 md:space-y-8 pr-20">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-800">System Dashboard</h1>
          <HelpIcon section="dashboard/production-status" tooltip="Learn about the dashboard" />
          
          <div className="flex items-center gap-2 ml-4">
            <span className={`inline-flex h-2 w-2 rounded-full ${
              connectionStatus === 'connected' ? 'bg-green-400' : 
              connectionStatus === 'slow' ? 'bg-yellow-400' : 'bg-red-400'
            } ${connectionStatus === 'connected' ? 'animate-pulse' : ''}`}></span>
            <span className="text-xs text-gray-500">
              {(pollingIntervalRef.current / 1000).toFixed(0)}s updates
              {lastResponseTimeRef.current > 0 && ` (${lastResponseTimeRef.current.toFixed(0)}ms)`}
            </span>
          </div>
        </div>
      </div>

      <ProductionStatus 
        productGroup={dashboardData?.data?.session}
        lineMode={dashboardData?.data?.status?.lineMode}
        currentShift={dashboardData?.data?.status?.currentShift}
        productionDate={dashboardData?.data?.status?.productionDate}
        currentTime={dashboardData?.data?.status?.currentTime}
      />

      {!activeSession ? (
        <div className="flex flex-col items-center justify-center py-16 px-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <div className="text-center max-w-md">
            <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Product Group Assigned</h3>
            <p className="text-gray-600 mb-6">
              Please select a product group using the Production Control panel on the right to start tracking production data.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {summaryCards.map(card => (
              <SummaryCard
                key={card.id}
                title={card.title}
                value={card.value}
                icon={card.icon}
                iconBgColor={card.iconBgColor}
                iconColor={card.iconColor}
              />
            ))}
          </div>

          {/* Optimized Data Table with proper React rendering */}
          <div className="bg-white shadow-sm rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{dataTableTitle}</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {dashboardData?.data?.columns?.map((col, idx) => (
                      <th
                        key={`${col.key}-${idx}`}
                        className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {col.label}
                        {col.key.includes('weight') && weightUnit === 'kg' && ' (KG)'}
                        {col.key.includes('weight') && weightUnit !== 'kg' && ' (LB)'}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dashboardData?.data?.stations?.map((station, stationIdx) => (
                    <tr key={`station-${station.stationnumber}-${stationIdx}`} className="hover:bg-gray-50">
                      {dashboardData?.data?.columns?.map((col, colIdx) => (
                        <td
                          key={`cell-${station.stationnumber}-${col.key}-${colIdx}`}
                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center"
                        >
                          {formatCellValue(station[col.key], col)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {(!dashboardData?.data?.stations || dashboardData.data.stations.length === 0) && (
                    <tr>
                      <td 
                        colSpan={dashboardData?.data?.columns?.length || 1} 
                        className="px-6 py-8 text-center text-sm text-gray-500"
                      >
                        No station data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardFinalOptimized;